import type { FormEvent, UIEvent } from 'react';
import type { Emoji, EmojiMartData } from '@emoji-mart/data';
import emojiRegex from 'emoji-regex';
import React, { memo, useEffect, useState } from '../../lib/teact/teact';

import buildClassName from '../../util/buildClassName';

import useLang from '../../hooks/useLang';
import useLastCallback from '../../hooks/useLastCallback';

import Modal from '../ui/Modal';
import Spinner from '../ui/Spinner';

import searchStyles from '../ui/SearchBar.module.scss';
import styles from './MetadataEmojiPicker.module.scss';

const GROUPS = ['people', 'nature', 'foods', 'activity', 'places', 'objects', 'symbols', 'flags'];
const GROUP_ICONS = ['\uD83D\uDE00', '\uD83D\uDC3E', '\uD83C\uDF54', '\u26BD',
  '\uD83C\uDFD9\uFE0F', '\uD83D\uDCA1', '\uD83D\uDD23', '\uD83D\uDEA9'];
const TONES = ['\uD83D\uDC4B', '\uD83D\uDC4B\uD83C\uDFFB', '\uD83D\uDC4B\uD83C\uDFFC',
  '\uD83D\uDC4B\uD83C\uDFFD', '\uD83D\uDC4B\uD83C\uDFFE', '\uD83D\uDC4B\uD83C\uDFFF'];
let catalogRequest: Promise<{ data: EmojiMartData; search: (query: string) => Promise<Emoji[]> }> | undefined;

function loadCatalog() {
  catalogRequest ??= Promise.all([import('@emoji-mart/data'), import('emoji-mart')]).then(async ([data, mart]) => {
    const catalog = data.default as unknown as EmojiMartData;
    await mart.init({ data: catalog });
    return { data: catalog, search: (query: string) => mart.SearchIndex.search(query,
      { maxResults: 500, caller: undefined }) };
  }).catch((error) => {
    catalogRequest = undefined;
    throw error;
  });
  return catalogRequest;
}

function MetadataEmojiPicker({ isOpen, onSelect, onClose }: {
  isOpen: boolean; onSelect: (emoji: string) => void; onClose: NoneToVoidFunction;
}) {
  const lang = useLang();
  const [catalog, setCatalog] = useState<Awaited<ReturnType<typeof loadCatalog>>>();
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState('people');
  const [tone, setTone] = useState(0);
  const [results, setResults] = useState<Emoji[]>([]);
  const [visibleCount, setVisibleCount] = useState(96);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!isOpen) return undefined;
    let isCurrent = true;
    setHasLoadError(false);
    void loadCatalog().then((next) => {
      if (isCurrent) setCatalog(next);
    }).catch(() => {
      if (isCurrent) setHasLoadError(true);
    });
    return () => {
      isCurrent = false;
    };
  }, [isOpen, retry]);
  useEffect(() => {
    if (!isOpen || !catalog) return undefined;
    let isCurrent = true;
    setVisibleCount(96);
    const text = query.trim();
    if (!text) {
      setResults((catalog.data.categories.find((category) => category.id === group)?.emojis ?? [])
        .map((id) => catalog.data.emojis[id]).filter(Boolean));
      return undefined;
    }
    const timer = window.setTimeout(() => {
      void catalog.search(text).then((next) => {
        if (isCurrent) setResults(next);
      })
        .catch(() => { if (isCurrent) setResults([]); });
    }, 150);
    return () => {
      isCurrent = false;
      window.clearTimeout(timer);
    };
  }, [isOpen, catalog, query, group]);

  const nativeQuery = query.trim();
  const customEmoji = emojiRegex().exec(nativeQuery)?.[0] === nativeQuery && nativeQuery ? nativeQuery : undefined;
  const handleScroll = useLastCallback((event: UIEvent<HTMLDivElement>) => {
    const element = event.currentTarget;
    if (element.scrollHeight - element.scrollTop - element.clientHeight < 160) {
      setVisibleCount((count) => count + 96);
    }
  });
  const select = useLastCallback((emoji: string) => {
    onSelect(emoji);
    onClose();
  });

  return (
    <Modal isOpen={isOpen} isCompact hasCloseButton title={lang('$metadata_choose_emoji')} onClose={onClose}>
      <div className={searchStyles.wrapper}>
        <div className={searchStyles.iconAddon}>
          <i className={buildClassName(searchStyles.icon, 'icon-search')} aria-hidden />
        </div>
        <input
          id="metadata-emoji-search"
          className={searchStyles.input}
          type="search"
          value={query}
          placeholder={lang('Search')}
          aria-label={lang('Search')}
          autoComplete="off"
          onInput={(event: FormEvent<HTMLInputElement>) => setQuery(event.currentTarget.value)}
        />
      </div>
      <div className={styles.groups} role="group" aria-label={lang('$metadata_choose_emoji')}>
        {GROUPS.map((id, index) => (
          <button
            key={id}
            type="button"
            className={styles.emoji}
            aria-label={lang(`$metadata_emoji_${id}`)}
            title={lang(`$metadata_emoji_${id}`)}
            aria-pressed={group === id}
            onClick={() => {
              setGroup(id);
              setQuery('');
            }}
          >{GROUP_ICONS[index]}
          </button>
        ))}
      </div>
      <div className={buildClassName(styles.results, 'custom-scroll')} onScroll={handleScroll}>
        <div className={styles.grid}>
          {customEmoji && (
            <button type="button" className={styles.emoji} aria-label={customEmoji} onClick={() => select(customEmoji)}>
              {customEmoji}
            </button>
          )}
          {results.slice(0, visibleCount).map((emoji) => {
            const native = (emoji.skins[tone] ?? emoji.skins[0]).native;
            return (
              <button
                key={emoji.id}
                type="button"
                className={styles.emoji}
                aria-label={native}
                onClick={() => select(native)}
              >
                {native}
              </button>
            );
          })}
        </div>
        {!catalog && !hasLoadError && <div className={styles.empty}><Spinner /></div>}
        {hasLoadError && (
          <button type="button" className={styles.retry} onClick={() => setRetry((value) => value + 1)}>
            <i className="icon-refresh" aria-hidden /> {lang('Try Again')}
          </button>
        )}
        {catalog && !results.length && !customEmoji && (
          <div className={styles.empty}>{lang('$metadata_no_matches')}</div>
        )}
      </div>
      <div className={styles.tones} role="group" aria-label={lang('$metadata_emoji_skin_tone')}>
        {TONES.map((emoji, index) => (
          <button
            key={emoji}
            type="button"
            className={styles.emoji}
            aria-label={`${lang('$metadata_emoji_skin_tone')} ${index + 1}`}
            title={`${lang('$metadata_emoji_skin_tone')} ${index + 1}`}
            aria-pressed={tone === index}
            onClick={() => setTone(index)}
          >
            {emoji}
          </button>
        ))}
      </div>
    </Modal>
  );
}

export default memo(MetadataEmojiPicker);
